<template>
  <div class="p-4 md:p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
    <!-- Encabezado -->
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
      <div class="flex items-center space-x-3">
        <div class="h-10 w-1 bg-gradient-to-b from-blue-500 to-indigo-600 rounded-full"></div>
        <div>
          <h1 class="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">Reporte de Ventas</h1>
          <p class="text-sm text-gray-500 dark:text-gray-400 flex items-center mt-1">
            <Icon icon="mdi:chart-line" class="mr-1" />
            SYSTEM JJ - Reportes de Ventas
          </p>
        </div>
      </div>
      <button
        @click="volver"
        class="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 shadow-sm hover:shadow flex items-center gap-2"
      >
        <Icon icon="mdi:arrow-left" class="text-lg" />
        Volver al panel
      </button>
    </div>

    <!-- Filtros -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-6 mb-6">
      <div class="flex flex-wrap gap-4 items-end">
        <div class="flex-1 min-w-[180px]">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tipo de Venta</label>
          <select v-model="filtros.tipoVenta" class="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200" @change="aplicarFiltros">
            <option value="todos">Todos</option>
            <option value="contado">Contado</option>
            <option value="credito">Crédito</option>
          </select>
        </div>

        <div class="flex-1 min-w-[200px]">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Método Pago</label>
          <select v-model="filtros.metodoPago" class="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200" @change="aplicarFiltros">
            <option value="todos">Todos</option>
            <option v-for="mp in metodosPagoDisponibles" :key="mp" :value="mp">{{ mp }}</option>
          </select>
        </div>

        <div class="flex-1 min-w-[200px]">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Cliente</label>
          <select v-model="filtros.idCliente" class="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200" @change="aplicarFiltros">
            <option value="todos">Todos los clientes</option>
            <option v-for="cliente in clientes" :key="cliente.idCliente" :value="cliente.idCliente">{{ cliente.nombreCompleto }}</option>
          </select>
        </div>

        <div class="flex-1 min-w-[200px]">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Vendedor</label>
          <select v-model="filtros.idVendedor" class="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200" @change="aplicarFiltros">
            <option value="todos">Todos los vendedores</option>
            <option v-for="vendedor in vendedores" :key="vendedor.idVendedor" :value="vendedor.idVendedor">{{ vendedor.nombreCompleto }}</option>
          </select>
        </div>

        <div class="flex-1 min-w-[180px]">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Desde</label>
          <input v-model="filtros.fechaDesde" type="date" class="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200" @change="aplicarFiltros" />
        </div>

        <div class="flex-1 min-w-[180px]">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Hasta</label>
          <input v-model="filtros.fechaHasta" type="date" class="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200" @change="aplicarFiltros" />
        </div>

        <div class="flex gap-2">
          <button @click="aplicarFiltros" class="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 font-medium shadow-md hover:shadow-lg flex items-center gap-2">
            <Icon icon="mdi:filter" class="text-lg" />
            Filtrar
          </button>
          <button @click="limpiarFiltros" class="px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-all duration-200 font-medium flex items-center gap-2">
            <Icon icon="mdi:refresh" class="text-lg" />
            Limpiar
          </button>
        </div>
      </div>
    </div>

    <!-- Panel de Distribución de Ingresos por Método de Pago -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-4 mb-6 border border-gray-100 dark:border-gray-700">
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-lg font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
          <div class="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
            <Icon icon="mdi:chart-pie" class="text-blue-600 dark:text-blue-400 text-xl" />
          </div>
          Distribución de Ingresos por Método de Pago y Créditos
        </h2>
        <div v-if="tasaUbcv" class="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 rounded-xl">
          <Icon icon="mdi:currency-usd" class="text-emerald-600 dark:text-emerald-400 text-sm" />
          <span class="text-xs font-bold text-emerald-700 dark:text-emerald-400">Tasa UBCV: Bs. {{ tasaUbcv.toFixed(2) }} por Dólar</span>
        </div>
      </div>
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div v-for="(val, metodo) in ingresosResumen" :key="metodo" class="p-4 rounded-2xl border-2 transition-all duration-200 hover:shadow-md" :class="getMetodoStyleVenta(metodo)">
          <div class="flex justify-between items-center mb-3">
            <span class="font-bold text-sm" :title="metodo">{{ metodo }}</span>
            <div class="p-2 rounded-xl bg-white/50 dark:bg-black/20">
              <Icon :icon="getMetodoIconVenta(metodo)" class="text-xl" />
            </div>
          </div>
          <div class="space-y-1">
            <div class="flex justify-between items-center">
              <span class="text-xs font-medium opacity-80">USD</span>
              <span class="font-black text-base">${{ val.usd.toFixed(2) }}</span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-xs font-medium opacity-80">Bs.</span>
              <span class="text-xs font-bold opacity-70">{{ tasaUbcv ? val.bs.toFixed(2) : '—' }}</span>
            </div>
          </div>
        </div>
        <div v-if="Object.keys(ingresosResumen).length === 0" class="col-span-full text-center py-8 text-gray-400 dark:text-gray-500">
          <Icon icon="mdi:chart-pie-outline" class="text-4xl mx-auto mb-2" />
          <p class="text-sm">No hay ingresos para los filtros seleccionados.</p>
        </div>
      </div>

      <div v-if="Object.keys(ingresosResumen).length > 0" class="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex flex-wrap gap-4 justify-end">
        <div class="flex items-center gap-2">
          <span class="text-xs font-bold text-gray-500 uppercase">Total USD:</span>
          <span class="text-sm font-black text-blue-600 dark:text-blue-400">
            ${{ Object.values(ingresosResumen).reduce((s, v) => s + v.usd, 0).toFixed(2) }}
          </span>
        </div>
        <div v-if="tasaUbcv" class="flex items-center gap-2">
          <span class="text-xs font-bold text-gray-500 uppercase">Total Bs.:</span>
          <span class="text-sm font-black text-emerald-600 dark:text-emerald-400">
            {{ Object.values(ingresosResumen).reduce((s, v) => s + v.bs, 0).toFixed(2) }}
          </span>
        </div>
      </div>
    </div>

    <!-- TOP PRODUCTOS -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-5 mb-6">
      <div class="flex items-center justify-between mb-5">
        <h2 class="text-lg font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
          <div class="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
            <Icon icon="mdi:trophy" class="text-amber-600 dark:text-amber-400 text-xl" />
          </div>
          Top Productos Más Vendidos
        </h2>
        <div class="flex items-center gap-2">
          <label class="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">Ordenar por:</label>
          <select v-model="ordenTopProductos" class="px-3 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 font-bold focus:ring-2 focus:ring-amber-500 outline-none">
            <option value="cantidad">Unidades vendidas</option>
            <option value="monto">Monto vendido (USD)</option>
            <option value="ganancia">Ganancia (USD)</option>
          </select>
        </div>
      </div>

      <div v-if="cargandoTopProductos" class="text-center py-8">
        <div class="inline-block animate-spin rounded-full h-6 w-6 border-3 border-amber-500 border-t-transparent"></div>
        <p class="text-gray-500 dark:text-gray-400 mt-2 text-sm">Calculando productos más vendidos...</p>
      </div>

      <div v-else-if="topProductos.length > 0" class="space-y-3">
        <div v-for="(prod, index) in topProductos" :key="prod.idProducto" class="relative p-4 bg-gray-50 dark:bg-gray-700/30 rounded-2xl border border-gray-100 dark:border-gray-700 hover:border-amber-300 dark:hover:border-amber-700 transition-all duration-200">
          <div class="flex items-center gap-4">
            <div class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-black text-sm" :class="getRankingClasses(index)">
              <Icon v-if="index === 0" icon="mdi:crown" class="text-lg" />
              <span v-else>#{{ index + 1 }}</span>
            </div>

            <div class="flex-1 min-w-0">
              <div class="flex items-start justify-between gap-3 mb-2">
                <div class="min-w-0">
                  <p class="font-bold text-sm text-gray-800 dark:text-white truncate">{{ prod.nombre }}</p>
                  <p class="text-[10px] text-gray-500 dark:text-gray-400 font-mono">{{ prod.codigo || '—' }}</p>
                </div>
                <div class="text-right flex-shrink-0">
                  <p class="text-base font-black text-amber-600 dark:text-amber-400">
                    {{ prod.cantidad }} <span class="text-[10px] font-bold text-gray-500">uds</span>
                  </p>
                  <p class="text-[10px] text-gray-500 dark:text-gray-400">{{ prod.ventasCount }} ventas</p>
                </div>
              </div>

              <div class="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2 mb-2 overflow-hidden">
                <div class="h-full rounded-full transition-all duration-500" :class="getBarraColor(index)" :style="{ width: calcularPorcentajeBarra(prod) + '%' }"></div>
              </div>

              <div class="flex flex-wrap gap-3 justify-between text-[10px] font-bold">
                <div class="flex items-center gap-1 text-blue-600 dark:text-blue-400">
                  <Icon icon="mdi:currency-usd" class="text-sm" />
                  <span>{{ formatMoney(prod.totalUsd) }} USD</span>
                </div>
                <div class="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                  <Icon icon="mdi:trending-up" class="text-sm" />
                  <span>{{ calcularPorcentajeBarra(prod).toFixed(1) }}% del total</span>
                </div>
                <div v-if="prod.gananciaUsd > 0" class="flex items-center gap-1 text-purple-600 dark:text-purple-400">
                  <Icon icon="mdi:cash-plus" class="text-sm" />
                  <span>Ganancia: {{ formatMoney(prod.gananciaUsd) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="text-center py-8">
        <Icon icon="mdi:package-variant-closed" class="text-4xl text-gray-300 dark:text-gray-600 mx-auto mb-2" />
        <p class="text-sm text-gray-500 dark:text-gray-400">No hay productos vendidos para los filtros seleccionados</p>
      </div>
    </div>

    <!-- Tarjetas de Estadísticas -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-5 hover:shadow-xl transition-all duration-200">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-500 dark:text-gray-400">Total Ventas</p>
            <p class="text-2xl font-bold text-gray-800 dark:text-white">{{ estadisticasFiltradas.total }}</p>
          </div>
          <div class="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
            <Icon icon="mdi:cart" class="text-2xl text-blue-600 dark:text-blue-400" />
          </div>
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-5 hover:shadow-xl transition-all duration-200">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-500 dark:text-gray-400">Total Facturado (Bs)</p>
            <p class="text-2xl font-bold text-green-600 dark:text-green-400">{{ formatearMonedaBs(estadisticasFiltradas.totalFacturado) }}</p>
          </div>
          <div class="p-3 bg-green-100 dark:bg-green-900/30 rounded-xl">
            <Icon icon="mdi:cash" class="text-2xl text-green-600 dark:text-green-400" />
          </div>
        </div>
      </div>

      <!-- GANANCIA TOTAL -->
      <div class="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-2xl shadow-lg border-2 border-emerald-200 dark:border-emerald-800 p-5 hover:shadow-xl transition-all duration-200">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-emerald-700 dark:text-emerald-400 font-bold uppercase tracking-wide">Ganancia Total</p>
            <p class="text-2xl font-black text-emerald-600 dark:text-emerald-400">
              ${{ estadisticasFiltradas.totalGanancia.toFixed(2) }}
            </p>
            <p class="text-[10px] font-bold text-emerald-600/70 dark:text-emerald-400/70 mt-1">
              Margen: {{ estadisticasFiltradas.margenPromedio.toFixed(1) }}%
            </p>
          </div>
          <div class="p-3 bg-emerald-100 dark:bg-emerald-900/40 rounded-xl">
            <Icon icon="mdi:cash-plus" class="text-2xl text-emerald-600 dark:text-emerald-400" />
          </div>
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-5 hover:shadow-xl transition-all duration-200">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-500 dark:text-gray-400">Ventas Contado</p>
            <p class="text-2xl font-bold text-orange-600 dark:text-orange-400">{{ estadisticasFiltradas.ventasContado }}</p>
          </div>
          <div class="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-xl">
            <Icon icon="mdi:cash-fast" class="text-2xl text-orange-600 dark:text-orange-400" />
          </div>
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-5 hover:shadow-xl transition-all duration-200">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-500 dark:text-gray-400">Ventas Crédito</p>
            <p class="text-2xl font-bold text-purple-600 dark:text-purple-400">{{ estadisticasFiltradas.ventasCredito }}</p>
          </div>
          <div class="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
            <Icon icon="mdi:credit-card" class="text-2xl text-purple-600 dark:text-purple-400" />
          </div>
        </div>
      </div>
    </div>

    <!-- Tabla de Ventas -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
      <div class="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-800 px-6 py-4 border-b border-gray-100 dark:border-gray-700">
        <div class="flex flex-wrap justify-between items-center gap-4">
          <h2 class="text-lg font-semibold text-gray-800 dark:text-white flex items-center">
            <div class="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg mr-3">
              <Icon icon="mdi:format-list-bulleted" class="text-blue-600 dark:text-blue-400 text-xl" />
            </div>
            Listado de Ventas
            <span v-if="ventasFiltradas.length > 0" class="ml-2 text-sm bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded-full">
              {{ ventasFiltradas.length }} registros
            </span>
          </h2>
          
          <div class="flex gap-2">
            <button
              @click="exportarExcel"
              :disabled="ventasFiltradas.length === 0"
              :class="[
                'px-4 py-2 rounded-xl transition-all duration-200 flex items-center gap-2 text-sm font-medium',
                ventasFiltradas.length > 0
                  ? 'bg-green-600 text-white hover:bg-green-700 shadow-md hover:shadow-lg'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              ]"
            >
              <Icon icon="mdi:file-excel" class="text-lg" />
              Exportar Excel
            </button>
            <button
              @click="imprimirReporte"
              :disabled="ventasFiltradas.length === 0"
              :class="[
                'px-4 py-2 rounded-xl transition-all duration-200 flex items-center gap-2 text-sm font-medium',
                ventasFiltradas.length > 0
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md hover:shadow-lg'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              ]"
            >
              <Icon icon="mdi:printer" class="text-lg" />
              Imprimir Reporte
            </button>
          </div>
        </div>
      </div>

      <div v-if="cargando" class="p-12 text-center">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-3 border-blue-500 border-t-transparent"></div>
        <p class="text-gray-500 dark:text-gray-400 mt-3">Cargando ventas...</p>
      </div>

      <div v-else-if="ventasFiltradas.length > 0" class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Factura</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Fecha</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tipo</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total USD</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total Bs</th>
              <!-- GANANCIA -->
              <th class="px-4 py-3 text-right text-xs font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-wider bg-emerald-50/50 dark:bg-emerald-900/10">Ganancia</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Método Pago</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Cliente</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Vendedor</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
            <tr v-for="venta in ventasFiltradas" :key="venta.idVenta" @click="verDetalles(venta)" class="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150 cursor-pointer">
              <td class="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                {{ venta.numeroFactura || '—' }}
              </td>
              <td class="px-4 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                {{ formatearFechaCorta(venta.fechaVenta) }}
              </td>
              <td class="px-4 py-4 whitespace-nowrap text-center">
                <span :class="getBadgeTipoVenta(venta.tipoVenta).clase" class="inline-block px-3 py-1 text-xs font-semibold rounded-full">
                  {{ getBadgeTipoVenta(venta.tipoVenta).texto }}
                </span>
              </td>
              <td class="px-4 py-4 whitespace-nowrap text-right">
                <div class="text-sm font-bold text-gray-700 dark:text-gray-300">$ {{ (venta.totalUsd || 0).toFixed(2) }}</div>
                <div v-if="esVentaCredito(venta)" class="text-[10px] font-black uppercase tracking-tighter" :class="(venta.montoPagado || 0) > 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'">
                  {{ (venta.montoPagado || 0) > 0 ? `Abonó: $${(venta.montoPagado).toFixed(2)}` : 'Sin abono' }}
                </div>
              </td>
              <td class="px-4 py-4 whitespace-nowrap text-right text-sm font-bold text-green-600 dark:text-green-400">
                {{ formatearMonedaBs(venta.totalBs || 0) }}
              </td>
              <!-- GANANCIA POR VENTA -->
              <td class="px-4 py-4 whitespace-nowrap text-right bg-emerald-50/30 dark:bg-emerald-900/5">
                <div class="text-sm font-black text-emerald-600 dark:text-emerald-400">
                  $ {{ (venta.gananciaNetaUsd || 0).toFixed(2) }}
                </div>
                <div v-if="venta.margenGananciaPorcentaje > 0" class="text-[9px] font-bold text-emerald-600/70 dark:text-emerald-400/70">
                  {{ (venta.margenGananciaPorcentaje || 0).toFixed(1) }}%
                </div>
              </td>
              <td class="px-4 py-4 text-xs font-bold text-gray-600 dark:text-gray-400">
                <span :class="venta.metodoPago?.includes('CRÉDITO') ? 'text-orange-600 dark:text-orange-400' : 'text-blue-600 dark:text-blue-400'">
                  {{ venta.metodoPago || '—' }}
                </span>
              </td>
              <td class="px-4 py-4 text-sm text-gray-600 dark:text-gray-400">
                {{ venta.clienteNombre || 'Consumidor Final' }}
              </td>
              <td class="px-4 py-4 text-sm text-gray-600 dark:text-gray-400">
                {{ venta.vendedorNombre || '—' }}
              </td>
            </tr>
          </tbody>
          <tfoot class="bg-gray-50 dark:bg-gray-700/50 font-bold border-t-2 border-gray-200 dark:border-gray-600">
            <tr class="h-8">
              <td colspan="3" class="px-4 py-1 text-right text-xs text-gray-500 uppercase whitespace-nowrap">
                Total Vendido:
              </td>
              <td class="px-4 py-1 text-right whitespace-nowrap text-sm text-gray-800 dark:text-white">
                ${{ estadisticasFiltradas.totalUSD.toFixed(2) }}
              </td>
              <td class="px-4 py-1 text-right whitespace-nowrap text-sm text-green-700 dark:text-green-400">
                {{ formatearMonedaBs(estadisticasFiltradas.totalFacturado) }}
              </td>
              <td class="px-4 py-1 text-right whitespace-nowrap text-sm font-black text-emerald-600 dark:text-emerald-400 bg-emerald-50/50 dark:bg-emerald-900/10">
                ${{ estadisticasFiltradas.totalGanancia.toFixed(2) }}
              </td>
              <td colspan="3" class="px-4 py-1"></td>
            </tr>
            <tr class="bg-green-50 dark:bg-green-900/10 border-t border-green-100 dark:border-green-800 h-10">
              <td colspan="3" class="px-4 py-1 text-right text-xs text-green-700 dark:text-green-400 uppercase font-black whitespace-nowrap">
                TOTAL COBRADO (CAJA):
              </td>
              <td class="px-4 py-1 text-right whitespace-nowrap text-sm font-black text-green-600 dark:text-green-400 border-l border-green-100 dark:border-green-800">
                ${{ (estadisticasFiltradas.totalCobradoUSD || 0).toFixed(2) }}
              </td>
              <td class="px-4 py-1 text-right whitespace-nowrap text-xs font-bold text-green-700 dark:text-green-400">
                Bs.{{ (estadisticasFiltradas.totalCobradoUSD * (ventasFiltradas[0]?.totalBs / ventasFiltradas[0]?.totalUsd || 1)).toFixed(2) }}
              </td>
              <td class="px-4 py-1 text-right whitespace-nowrap text-xs font-black text-emerald-700 dark:text-emerald-400 bg-emerald-50/50 dark:bg-emerald-900/10">
                Margen: {{ estadisticasFiltradas.margenPromedio.toFixed(1) }}%
              </td>
              <td colspan="3" class="px-4 py-1"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div v-else class="text-center py-12">
        <div class="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
          <Icon icon="mdi:chart-line" class="text-4xl text-gray-400" />
        </div>
        <p class="text-gray-600 dark:text-gray-400 font-medium">No hay ventas para mostrar</p>
        <p class="text-sm text-gray-500 dark:text-gray-500 mt-1">Prueba cambiando los filtros</p>
      </div>
    </div>

    <!-- MODAL DE DETALLES -->
    <Teleport to="body">
      <div v-if="modalDetalles.show" class="fixed inset-0 z-50 overflow-hidden flex items-center justify-center p-2 sm:p-4" role="dialog">
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="cerrarModalDetalles"></div>
        <div class="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col overflow-hidden transform transition-all animate-fadeIn max-h-[90vh]">
          <div class="px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="p-2 rounded-xl bg-white/20">
                <Icon icon="mdi:receipt-text" class="text-white text-xl" />
              </div>
              <div>
                <h3 class="text-base font-bold text-white uppercase tracking-wider">{{ ventaSeleccionada?.numeroFactura }}</h3>
                <p class="text-[10px] text-blue-100 uppercase font-medium">{{ formatFechaCompleta(ventaSeleccionada?.fechaVenta) }}</p>
              </div>
            </div>
            <button @click="cerrarModalDetalles" class="text-white/80 hover:text-white transition-colors">
              <Icon icon="mdi:close" class="text-xl" />
            </button>
          </div>

          <div class="flex bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 p-1">
            <button v-for="tab in ['resumen', 'productos', 'pagos', 'observaciones']" :key="tab" @click="tabActiva = tab" :class="['flex-1 py-2 text-[10px] font-black uppercase rounded-lg transition-all', tabActiva === tab ? 'bg-white dark:bg-gray-700 text-blue-600 shadow-sm' : 'text-gray-400 hover:text-gray-600']">{{ tab }}</button>
          </div>

          <div class="flex-1 overflow-y-auto p-6">
            <div v-if="tabActiva === 'resumen'" class="space-y-4 animate-fadeIn">
              <div class="grid grid-cols-2 gap-4">
                <div class="p-4 bg-gray-50 dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700">
                  <span class="text-[10px] font-bold text-gray-400 uppercase block mb-1">Cliente</span>
                  <span class="text-sm font-black text-gray-800 dark:text-white">{{ ventaSeleccionada?.clienteNombre }}</span>
                </div>
                <div class="p-4 bg-gray-50 dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700">
                  <span class="text-[10px] font-bold text-gray-400 uppercase block mb-1">Vendedor</span>
                  <span class="text-sm font-black text-gray-800 dark:text-white">{{ ventaSeleccionada?.vendedorNombre }}</span>
                </div>
              </div>
              <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div class="p-3 bg-blue-500/5 rounded-xl text-center">
                   <p class="text-[9px] font-bold text-blue-400 uppercase mb-1">Total USD</p>
                   <p class="text-sm font-black text-blue-600">${{ formatNumber(ventaSeleccionada?.totalUsd) }}</p>
                </div>
                <div class="p-3 bg-green-500/5 rounded-xl text-center">
                   <p class="text-[9px] font-bold text-green-400 uppercase mb-1">Estado</p>
                   <p class="text-[10px] font-black text-green-600 uppercase">{{ ventaSeleccionada?.estado }}</p>
                </div>
                <div class="p-3 bg-emerald-500/5 rounded-xl text-center">
                   <p class="text-[9px] font-bold text-emerald-400 uppercase mb-1">Ganancia</p>
                   <p class="text-sm font-black text-emerald-600">${{ formatNumber(ventaSeleccionada?.gananciaNetaUsd) }}</p>
                </div>
                <div class="p-3 bg-indigo-500/5 rounded-xl text-center">
                   <p class="text-[9px] font-bold text-indigo-400 uppercase mb-1">Método</p>
                   <p class="text-[10px] font-black text-indigo-600 uppercase">{{ ventaSeleccionada?.metodoPago || 'Efectivo' }}</p>
                </div>
              </div>
            </div>

            <div v-if="tabActiva === 'productos'" class="space-y-2 animate-fadeIn">
              <div v-for="detalle in ventaSeleccionada?.detalles" :key="detalle.idVentaDetalle" class="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700">
                <div class="flex-1">
                  <p class="text-xs font-bold text-gray-800 dark:text-white uppercase">{{ detalle.nombreProducto || detalle.productoNombre }}</p>
                  <p class="text-[10px] text-gray-400 font-bold uppercase">{{ detalle.cantidad }} Uds x ${{ formatNumber(detalle.precioUnitarioUsd) }}</p>
                </div>
                <div class="text-right">
                  <p class="text-sm font-black text-blue-600">${{ formatNumber(detalle.subtotalUsd) }}</p>
                  <p v-if="detalle.gananciaTotalUsd" class="text-[10px] font-bold text-emerald-600">
                    Ganancia: ${{ formatNumber(detalle.gananciaTotalUsd) }}
                  </p>
                </div>
              </div>
            </div>

            <div v-if="tabActiva === 'pagos'" class="space-y-4 animate-fadeIn">
               <div v-if="ventaSeleccionada?.pagos?.length > 0">
                 <div v-for="(pago, i) in ventaSeleccionada.pagos" :key="i" class="p-4 rounded-2xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-sm">
                   <div class="flex justify-between items-center border-b border-gray-50 dark:border-gray-700 pb-2 mb-2">
                     <span class="text-[10px] font-black text-indigo-500 uppercase">Recibo #{{ i+1 }}</span>
                     <span class="text-[10px] font-bold text-gray-400">{{ formatFechaCompleta(pago.fechaPago) }}</span>
                   </div>
                   <div class="flex justify-between items-end">
                     <div>
                       <p class="text-[10px] font-bold text-gray-400 uppercase">Vía: {{ pago.tipoPago }}</p>
                     </div>
                     <p class="text-lg font-black text-green-600">${{ formatNumber(pago.montoUsd) }}</p>
                   </div>
                 </div>
               </div>
               <div v-else class="text-center py-10">
                 <Icon icon="mdi:cash-off" class="text-4xl text-gray-200 mx-auto mb-2" />
                 <p class="text-[10px] font-bold text-gray-400 uppercase">No se registran pagos directos</p>
               </div>
            </div>

             <div v-if="tabActiva === 'observaciones'" class="p-4 bg-gray-50 dark:bg-gray-800 rounded-2xl min-h-[100px] border border-gray-100 dark:border-gray-700 animate-fadeIn">
               <p class="text-xs text-gray-600 dark:text-gray-300">{{ ventaSeleccionada?.observaciones || 'Sin comentarios adicionales.' }}</p>
            </div>
          </div>

          <div class="p-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50 flex justify-end gap-2">
            <button @click="cerrarModalDetalles" class="px-6 py-2 text-[10px] font-black text-gray-400 uppercase hover:text-gray-600">Cerrar Detalle</button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { useToast } from "vue-toastification";
import { obtenerVentas, obtenerVentaConRelaciones } from '@/api/ventas.js';
import { obtenerPagosPorVenta } from '@/api/pagoVenta.js';
import { obtenerVendedores } from '@/api/vendedores.js';
import { obtenerClientes } from '@/api/clientes.js';
import { obtenerTasaVentaActual } from '@/api/tasaVenta.js';
import { 
  imprimirReporteVentas, 
  exportarVentasExcel, 
  formatearFechaCorta, 
  formatearMonedaBs, 
  getBadgeTipoVenta, 
  getBadgeEstadoVenta
} from '@/utils/reporteVentasGenerator.js';

export default {
  name: 'ReporteVentas',
  components: { Icon },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      cargando: false,
      ventas: [],
      ventasFiltradas: [],
      vendedores: [],
      clientes: [],
      vendedoresMap: new Map(),
      clientesMap: new Map(),
      tasaUbcv: null,
      ingresosResumen: {},
      filtros: {
        tipoVenta: 'todos',
        estado: 'todos',
        metodoPago: 'todos',
        idCliente: 'todos',
        idVendedor: 'todos',
        fechaDesde: new Date().toLocaleDateString('sv-SE'),
        fechaHasta: ''
      },
      estadisticasFiltradas: {
        total: 0,
        totalFacturado: 0,
        totalUSD: 0,
        totalCosto: 0,
        totalGanancia: 0,
        margenPromedio: 0,
        ventasContado: 0,
        ventasCredito: 0,
        totalCobradoUSD: 0
      },
      topProductos: [],
      cargandoTopProductos: false,
      ordenTopProductos: 'cantidad',
      modalDetalles: { show: false },
      ventaSeleccionada: null,
      tabActiva: 'resumen'
    };
  },
  computed: {
    metodosPagoDisponibles() {
      const set = new Set();
      this.ventas.forEach(v => {
        const mp = (v.metodoPago || '').toString().trim();
        if (mp && mp !== '—') {
          set.add(this.normalizarMetodoPagoLabel(mp));
        }
      });
      return Array.from(set).sort();
    }
  },
  async mounted() {
    await this.cargarVendedores();
    await this.cargarClientes();
    await this.cargarTasaUbcv();
    await this.cargarVentas();
  },
  methods: {
    formatearFechaCorta,
    formatearMonedaBs,
    getBadgeTipoVenta,
    getBadgeEstadoVenta,

    normalizarMetodoPagoLabel(mp) {
      const s = (mp || '').toString().toUpperCase().trim();
      if (!s) return '';
      if (s.includes('CACHEA') || s.includes('CAS')) return 'CRÉDITO (CACHEAS)';
      if (s.includes('EMPRESA') || s.includes('CORP')) return 'CRÉDITO (EMPRESA)';
      if (s.includes('CRÉDITO') || s.includes('CREDITO')) return 'CRÉDITO';
      if (s.includes('PAGO M') || s.includes('MÓVIL') || s.includes('MOVIL')) return 'PAGO MÓVIL';
      if (s.includes('TRANSFERENCIA')) return 'TRANSFERENCIA';
      if (s.includes('PUNTO')) return 'PUNTO DE VENTA';
      if (s.includes('DEPÓSITO') || s.includes('DEPOSITO')) return 'DEPÓSITO';
      if (s.includes('EFECTIVO') || s.includes('CASH')) return 'EFECTIVO';
      return s;
    },

    async cargarTasaUbcv() {
      try {
        const res = await obtenerTasaVentaActual();
        if (res.success && res.data && res.data.precio_usd_bs) {
          this.tasaUbcv = parseFloat(res.data.precio_usd_bs);
        } else {
          this.tasaUbcv = null;
        }
      } catch (e) {
        console.error('Error cargando tasa UBCV en ventas', e);
        this.tasaUbcv = null;
      }
    },

    actualizarResumenVentas() {
      const resumen = {};
      this.ventasFiltradas.forEach(v => {
        let metodo = (v.metodoPago || 'EFECTIVO').toString().toUpperCase();
        if (metodo.includes('CRÉDITO') || metodo.includes('CREDITO')) {
          if (metodo.includes('CACHEA') || metodo.includes('CAS')) metodo = 'CRÉDITO (CACHEAS)';
          else if (metodo.includes('EMPRESA') || metodo.includes('CORP')) metodo = 'CRÉDITO (EMPRESA)';
          else metodo = 'CRÉDITO';
        } else if (metodo.includes('MOVIL') || metodo.includes('MÓVIL') || metodo.includes('PAGO M')) {
          metodo = 'PAGO MÓVIL';
        } else if (metodo.includes('TRANSFERENCIA')) {
          metodo = 'TRANSFERENCIA';
        } else if (metodo.includes('PUNTO')) {
          metodo = 'PUNTO DE VENTA';
        } else if (metodo.includes('EFECTIVO') || metodo.includes('CASH')) {
          metodo = 'EFECTIVO';
        }

        const usd = this.esVentaCredito(v)
          ? (parseFloat(v.montoPagado) || 0)
          : (parseFloat(v.totalUsd) || 0);
        const bs = this.tasaUbcv ? usd * this.tasaUbcv : 0;

        if (!resumen[metodo]) resumen[metodo] = { usd: 0, bs: 0 };
        resumen[metodo].usd += usd;
        resumen[metodo].bs += bs;
      });
      Object.values(resumen).forEach(val => {
        val.usd = parseFloat(val.usd.toFixed(2));
        val.bs = parseFloat(val.bs.toFixed(2));
      });
      this.ingresosResumen = resumen;
    },

    getMetodoStyleVenta(metodo) {
      const m = (metodo || '').toLowerCase();
      if (m.includes('cashea') || m.includes('cachea')) return 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400';
      if (m.includes('movil') || m.includes('móvil') || m.includes('pago m')) return 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400';
      if (m.includes('transferencia')) return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400';
      if (m.includes('punto')) return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      if (m.includes('credito') || m.includes('crédito') || m.includes('empresa')) return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
      if (m.includes('efectivo') || m.includes('cash')) return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    },

    getMetodoIconVenta(metodo) {
      const m = (metodo || '').toLowerCase();
      if (m.includes('cashea') || m.includes('cachea')) return 'mdi:lightning-bolt';
      if (m.includes('movil') || m.includes('móvil') || m.includes('pago m')) return 'mdi:cellphone';
      if (m.includes('transferencia')) return 'mdi:bank-transfer';
      if (m.includes('punto')) return 'mdi:credit-card-outline';
      if (m.includes('credito') || m.includes('crédito') || m.includes('empresa')) return 'mdi:domain';
      if (m.includes('efectivo') || m.includes('cash')) return 'mdi:cash';
      return 'mdi:cash-multiple';
    },

    // TOP PRODUCTOS
    getRankingClasses(index) {
      if (index === 0) return 'bg-gradient-to-br from-yellow-400 to-amber-500 text-white shadow-lg shadow-amber-500/30';
      if (index === 1) return 'bg-gradient-to-br from-gray-300 to-gray-400 text-white shadow-md';
      if (index === 2) return 'bg-gradient-to-br from-orange-400 to-orange-500 text-white shadow-md';
      return 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400';
    },

    getBarraColor(index) {
      if (index === 0) return 'bg-gradient-to-r from-yellow-400 to-amber-500';
      if (index === 1) return 'bg-gradient-to-r from-gray-400 to-gray-500';
      if (index === 2) return 'bg-gradient-to-r from-orange-400 to-orange-500';
      if (index < 5) return 'bg-gradient-to-r from-blue-500 to-indigo-500';
      return 'bg-gradient-to-r from-emerald-400 to-teal-500';
    },

    calcularPorcentajeBarra(prod) {
      if (!this.topProductos.length) return 0;
      let maxValor = 0;
      if (this.ordenTopProductos === 'cantidad') maxValor = Math.max(...this.topProductos.map(p => p.cantidad));
      else if (this.ordenTopProductos === 'monto') maxValor = Math.max(...this.topProductos.map(p => p.totalUsd));
      else maxValor = Math.max(...this.topProductos.map(p => p.gananciaUsd));
      if (maxValor === 0) return 0;
      
      let valor = 0;
      if (this.ordenTopProductos === 'cantidad') valor = prod.cantidad;
      else if (this.ordenTopProductos === 'monto') valor = prod.totalUsd;
      else valor = prod.gananciaUsd;
      
      return Math.min(100, (valor / maxValor) * 100);
    },

    formatMoney(v) {
      const num = parseFloat(v) || 0;
      return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    async calcularTopProductos() {
      if (!this.ventasFiltradas.length) {
        this.topProductos = [];
        return;
      }

      this.cargandoTopProductos = true;
      try {
        const productosMap = new Map();

        const promesas = this.ventasFiltradas.map(async (venta) => {
          try {
            const res = await obtenerVentaConRelaciones(venta.idVenta);
            if (!res.success || !res.data) return;

            const detalles = res.data.detalles || res.data.ventaDetalles || [];

            detalles.forEach(d => {
              const idProducto = d.idProducto;
              if (!idProducto) return;

              const cantidad = parseInt(d.cantidad) || 0;
              const subtotalUsd = parseFloat(d.subtotalUsd) || 0;
              const costoTotalUsd = parseFloat(d.costoTotalUsd) || 0;
              const gananciaTotalUsd = parseFloat(d.gananciaTotalUsd) || (subtotalUsd - costoTotalUsd);

              const nombre = d.nombreProducto || d.productoNombre || `Producto ${idProducto}`;
              const codigo = d.productoCodigo || d.codigo || '';

              if (!productosMap.has(idProducto)) {
                productosMap.set(idProducto, {
                  idProducto, nombre, codigo,
                  cantidad: 0, totalUsd: 0, gananciaUsd: 0, ventasCount: 0
                });
              }

              const prod = productosMap.get(idProducto);
              prod.cantidad += cantidad;
              prod.totalUsd += subtotalUsd;
              prod.gananciaUsd += gananciaTotalUsd;
              prod.ventasCount += 1;
            });
          } catch (err) {
            console.warn(`No se pudieron cargar detalles de venta ${venta.idVenta}:`, err.message);
          }
        });

        await Promise.all(promesas);

        let productos = Array.from(productosMap.values());

        if (this.ordenTopProductos === 'cantidad') productos.sort((a, b) => b.cantidad - a.cantidad);
        else if (this.ordenTopProductos === 'monto') productos.sort((a, b) => b.totalUsd - a.totalUsd);
        else productos.sort((a, b) => b.gananciaUsd - a.gananciaUsd);

        this.topProductos = productos.slice(0, 10).map(p => ({
          ...p,
          totalUsd: parseFloat(p.totalUsd.toFixed(2)),
          gananciaUsd: parseFloat(p.gananciaUsd.toFixed(2))
        }));
      } catch (e) {
        console.error('Error calculando top productos:', e);
        this.topProductos = [];
      } finally {
        this.cargandoTopProductos = false;
      }
    },

    async cargarVendedores() {
      try {
        const response = await obtenerVendedores();
        if (response.success) {
          this.vendedores = (response.data || []).map(v => ({
            idVendedor: v.idVendedor,
            nombreCompleto: `${v.nombre} ${v.apellido || ''}`.trim()
          }));
          this.vendedores.forEach(v => this.vendedoresMap.set(v.idVendedor, v.nombreCompleto));
        }
      } catch (error) {
        console.error('Error cargando vendedores:', error);
      }
    },

    async cargarClientes() {
      try {
        const response = await obtenerClientes();
        if (response.success) {
          this.clientes = (response.data || []).map(c => ({
            idCliente: c.idCliente,
            nombreCompleto: `${c.nombre} ${c.apellido || ''}`.trim()
          }));
          this.clientes.forEach(c => this.clientesMap.set(c.idCliente, c.nombreCompleto));
        }
      } catch (error) {
        console.error('Error cargando clientes:', error);
      }
    },

    // ✅ AQUÍ ESTÁ LA CLAVE: calcular ganancia si no viene
    normalizarVenta(venta) {
      const vendedorNombre = this.vendedoresMap.get(venta.idVendedor) || `Vendedor ${venta.idVendedor}`;
      const clienteNombre = this.clientesMap.get(venta.idCliente) || `Cliente ${venta.idCliente}`;
      
      const totalUsd = parseFloat(venta.totalUsd) || 0;
      const costoTotalUsd = parseFloat(venta.costoTotalUsd) || 0;
      
      // ✅ GANANCIA: si viene del backend la usamos, si no la calculamos
      let gananciaNetaUsd = parseFloat(venta.gananciaNetaUsd);
      if (!gananciaNetaUsd || gananciaNetaUsd === 0) {
        gananciaNetaUsd = totalUsd - costoTotalUsd;
      }
      
      // Margen
      let margenGananciaPorcentaje = parseFloat(venta.margenGananciaPorcentaje) || 0;
      if (!margenGananciaPorcentaje && costoTotalUsd > 0) {
        margenGananciaPorcentaje = (gananciaNetaUsd / costoTotalUsd) * 100;
      }
      
      return {
        idVenta: venta.idVenta,
        numeroFactura: venta.numeroFactura,
        fechaVenta: venta.fechaVenta,
        tipoVenta: (venta.tipoVenta || '').toString(),
        idTipoCredito: venta.idTipoCredito || venta.id_tipo_credito || venta.tipo_credito_id,
        totalUsd: totalUsd,
        totalBs: parseFloat(venta.totalBs) || 0,
        // GANANCIA
        costoTotalUsd: costoTotalUsd,
        gananciaNetaUsd: gananciaNetaUsd,
        margenGananciaPorcentaje: margenGananciaPorcentaje,
        estado: venta.estado,
        idCliente: venta.idCliente,
        clienteNombre: clienteNombre,
        idVendedor: venta.idVendedor,
        vendedorNombre: vendedorNombre,
        metodoPago: '—',
        montoPagado: 0
      };
    },

    async cargarVentas() {
      this.cargando = true;
      try {
        const response = await obtenerVentas();
        if (response.success) {
          this.ventas = (response.data || []).map(v => this.normalizarVenta(v));
          this.aplicarFiltros();
          this.cargarMetodosPago();
        } else {
          this.toast.error('Error al cargar las ventas');
        }
      } catch (error) {
        console.error('Error cargando ventas:', error);
        this.toast.error('Error al cargar las ventas');
      } finally {
        this.cargando = false;
      }
    },

    async cargarMetodosPago() {
      await Promise.all(this.ventas.map(async (venta) => {
        try {
          const vTipo = (venta.tipoVenta || '').toUpperCase();
          const resPagos = await obtenerPagosPorVenta(venta.idVenta);
          let pagosData = (resPagos.success && resPagos.data) ? resPagos.data : [];
          
          if (vTipo.includes('CREDITO') || vTipo.includes('CRÉDITO')) {
            const idC = (venta.idTipoCredito || '').toString().toUpperCase();
            let label = '';
            if (idC.includes('CAS') || idC.includes('CACHEA')) label = 'CACHEAS';
            else if (idC.includes('EMP') || idC.includes('CORP')) label = 'EMPRESA';
            else label = 'CRÉDITO';
            
            venta.tipoVenta = 'CRÉDITO';

            if (pagosData.length > 0) {
              const p = pagosData[0];
              const mp = (p.tipoPago || p.metodo_pago || 'efectivo').toLowerCase();
              venta.metodoPago = `${label} + ${mp}`;
            } else {
              venta.metodoPago = label;
            }
          } else {
            if (pagosData.length > 0) {
              const p = pagosData[pagosData.length - 1];
              venta.metodoPago = (p.tipoPago || p.metodo_pago || 'efectivo').toLowerCase();
            } else {
              venta.metodoPago = 'efectivo';
            }
          }
          venta.montoPagado = pagosData.reduce((sum, p) => sum + (parseFloat(p.montoUsd || p.monto_usd) || 0), 0);
        } catch (err) {
          venta.metodoPago = '—';
          venta.montoPagado = 0;
        }
      }));
      this.calcularEstadisticasFiltradas();
      this.actualizarResumenVentas();
    },

    aplicarFiltros() {
      let ventasFiltradas = [...this.ventas];

      if (this.filtros.tipoVenta !== 'todos') {
        ventasFiltradas = ventasFiltradas.filter(v => {
          if (this.filtros.tipoVenta === 'contado') return !this.esVentaCredito(v);
          return this.esVentaCredito(v);
        });
      }

      if (this.filtros.estado !== 'todos') {
        ventasFiltradas = ventasFiltradas.filter(v => (v.estado || '') === this.filtros.estado);
      }

      if (this.filtros.metodoPago !== 'todos') {
        ventasFiltradas = ventasFiltradas.filter(v => {
          const label = this.normalizarMetodoPagoLabel(v.metodoPago || '');
          return label === this.filtros.metodoPago;
        });
      }

      if (this.filtros.idCliente !== 'todos') {
        ventasFiltradas = ventasFiltradas.filter(v => v.idCliente == this.filtros.idCliente);
      }

      if (this.filtros.idVendedor !== 'todos') {
        ventasFiltradas = ventasFiltradas.filter(v => v.idVendedor == this.filtros.idVendedor);
      }

      const fdStr = this.filtros.fechaDesde;
      const fhStr = this.filtros.fechaHasta;

      if (fdStr && !fhStr) {
        ventasFiltradas = ventasFiltradas.filter(v => {
          const match = String(v.fechaVenta || '').match(/^(\d{4}-\d{2}-\d{2})/);
          return (match ? match[1] : '') === fdStr;
        });
      } else {
        if (fdStr) {
          ventasFiltradas = ventasFiltradas.filter(v => {
            const match = String(v.fechaVenta || '').match(/^(\d{4}-\d{2}-\d{2})/);
            return (match ? match[1] : '') >= fdStr;
          });
        }
        if (fhStr) {
          ventasFiltradas = ventasFiltradas.filter(v => {
            const match = String(v.fechaVenta || '').match(/^(\d{4}-\d{2}-\d{2})/);
            return (match ? match[1] : '') <= fhStr;
          });
        }
      }

      this.ventasFiltradas = ventasFiltradas;
      this.calcularEstadisticasFiltradas();
      this.actualizarResumenVentas();
      this.calcularTopProductos();
    },

    // ✅ CALCULAR ESTADÍSTICAS CON GANANCIA
    calcularEstadisticasFiltradas() {
      const total = this.ventasFiltradas.length;
      
      const ventasContado = this.ventasFiltradas.filter(v => !this.esVentaCredito(v)).length;
      const ventasCredito = this.ventasFiltradas.filter(v => this.esVentaCredito(v)).length;
      
      const totalFacturado = this.ventasFiltradas.reduce((sum, v) => sum + (v.totalBs || 0), 0);
      const totalUSD = this.ventasFiltradas.reduce((sum, v) => sum + (v.totalUsd || 0), 0);
      
      // ✅ GANANCIA: suma de ganancias por venta
      let totalGanancia = 0;
      let totalCosto = 0;
      this.ventasFiltradas.forEach(v => {
        const ganancia = parseFloat(v.gananciaNetaUsd) || 0;
        const costo = parseFloat(v.costoTotalUsd) || 0;
        totalGanancia += ganancia;
        totalCosto += costo;
      });
      
      const margenPromedio = totalCosto > 0 ? (totalGanancia / totalCosto) * 100 : 0;
      
      const totalCobradoUSD = this.ventasFiltradas.reduce((sum, v) => {
        if (this.esVentaCredito(v)) return sum + (parseFloat(v.montoPagado) || 0);
        return sum + (v.totalUsd || 0);
      }, 0);
      
      this.estadisticasFiltradas = {
        total,
        totalFacturado,
        totalUSD,
        totalCosto,
        totalGanancia: parseFloat(totalGanancia.toFixed(2)),
        margenPromedio: parseFloat(margenPromedio.toFixed(2)),
        ventasContado,
        ventasCredito,
        totalCobradoUSD
      };
    },

    esVentaCredito(venta) {
      const tipo = (venta.tipoVenta || '').toLowerCase();
      return tipo.includes('credito') || tipo.includes('crédito');
    },

    limpiarFiltros() {
      const today = new Date().toLocaleDateString('sv-SE');
      this.filtros = {
        tipoVenta: 'todos',
        estado: 'todos',
        metodoPago: 'todos',
        idCliente: 'todos',
        idVendedor: 'todos',
        fechaDesde: today,
        fechaHasta: ''
      };
      this.aplicarFiltros();
      this.toast.info('Filtros limpiados');
    },

    async exportarExcel() {
      if (this.ventasFiltradas.length === 0) {
        this.toast.warning('No hay ventas para exportar');
        return;
      }

      try {
        const titulo = this.obtenerTituloReporte();
        const ventasConGanancia = this.ventasFiltradas.map(v => ({
          ...v,
          gananciaNetaUsd: v.gananciaNetaUsd || ((v.totalUsd || 0) - (v.costoTotalUsd || 0))
        }));
        const result = exportarVentasExcel(ventasConGanancia, titulo);
        if (result.success) {
          this.toast.success(`Exportadas ${result.count} ventas a Excel`);
        } else {
          throw new Error(result.error);
        }
      } catch (error) {
        console.error('Error exportando:', error);
        this.toast.error('Error al exportar a Excel');
      }
    },

    async imprimirReporte() {
      if (this.ventasFiltradas.length === 0) {
        this.toast.warning('No hay ventas para imprimir');
        return;
      }

      try {
        const titulo = this.obtenerTituloReporte();
        const ventasParaReporte = this.ventasFiltradas.map(v => ({
          ...v,
          tipoVenta: v.metodoPago?.includes('CRÉDITO') ? v.metodoPago : v.tipoVenta,
          gananciaNetaUsd: v.gananciaNetaUsd || ((v.totalUsd || 0) - (v.costoTotalUsd || 0))
        }));
        await imprimirReporteVentas(ventasParaReporte, titulo);
      } catch (error) {
        console.error('Error imprimiendo:', error);
        this.toast.error('Error al imprimir el reporte');
      }
    },

    obtenerTituloReporte() {
      let titulo = 'REPORTE DE VENTAS';
      const partes = [];
      
      if (this.filtros.tipoVenta !== 'todos') partes.push(this.filtros.tipoVenta === 'contado' ? 'CONTADO' : 'CRÉDITO');
      if (this.filtros.metodoPago !== 'todos') partes.push(`MÉTODO: ${this.filtros.metodoPago}`);
      if (this.filtros.estado !== 'todos') {
        let estadoTexto = this.filtros.estado.toUpperCase();
        if (estadoTexto === 'COMPLETADA') estadoTexto = 'PAGADAS';
        partes.push(estadoTexto);
      }
      if (this.filtros.idCliente !== 'todos') {
        const cliente = this.clientes.find(c => c.idCliente == this.filtros.idCliente);
        if (cliente) partes.push(`CLIENTE: ${cliente.nombreCompleto}`);
      }
      if (this.filtros.idVendedor !== 'todos') {
        const vendedor = this.vendedores.find(v => v.idVendedor == this.filtros.idVendedor);
        if (vendedor) partes.push(`VENDEDOR: ${vendedor.nombreCompleto}`);
      }
      if (this.filtros.fechaDesde || this.filtros.fechaHasta) {
        partes.push(`${this.filtros.fechaDesde || 'INICIO'} al ${this.filtros.fechaHasta || 'HOY'}`);
      }
      
      if (partes.length > 0) titulo += ` - ${partes.join(' - ')}`;
      return titulo;
    },

    volver() {
      this.$router.push('/bienvenida');
    },

    async verDetalles(v) {
      this.ventaSeleccionada = { ...v };
      await this.cargarDetallesVenta(this.ventaSeleccionada);
      this.tabActiva = 'resumen';
      this.modalDetalles.show = true;
    },
    cerrarModalDetalles() { 
      this.modalDetalles.show = false; 
      setTimeout(() => { this.ventaSeleccionada = null; }, 300); 
    },
    async cargarDetallesVenta(venta) {
      try {
        const res = await obtenerVentaConRelaciones(venta.idVenta);
        if (res.success && res.data) {
          venta.detalles = res.data.detalles || [];
          if (!venta.pagos && res.data.pagos) venta.pagos = res.data.pagos;
          venta.observaciones = res.data.observaciones;
          if (!venta.gananciaNetaUsd && res.data.gananciaNetaUsd) {
            venta.gananciaNetaUsd = res.data.gananciaNetaUsd;
          }
          if (!venta.costoTotalUsd && res.data.costoTotalUsd) {
            venta.costoTotalUsd = res.data.costoTotalUsd;
          }
          return true;
        }
        return false;
      } catch (e) {
        console.error('Error cargando detalles:', e);
        return false;
      }
    },
    formatFechaCompleta(fecha) {
      if (!fecha) return '—';
      return new Date(fecha).toLocaleDateString('es-ES', { 
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
      });
    },
    formatNumber(v) { 
      return v === null || v === undefined ? '0.00' : parseFloat(v).toFixed(2); 
    }
  },
  watch: {
    ordenTopProductos() {
      this.calcularTopProductos();
    }
  }
};
</script>

<style scoped>
.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
</style>